import { useState, useEffect } from "react";
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  CssBaseline,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import { useLocation } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";

const cacheRtl = createCache({
  key: "muirtl",
  prepend: true,
});

const theme = createTheme({
  direction: "rtl",
  typography: {
    fontFamily: "Cairo, sans-serif",
  },
});

const LeaveRequestForm = ({ isAdmin = false, onSuccess }) => {
  const location = useLocation();
  const username = location.state?.username;
  // const user = JSON.parse(localStorage.getItem("data")) || {};
  const userData = localStorage.getItem("data");
  const user = userData && userData !== "undefined" ? JSON.parse(userData) : {};

  const [leaveData, setLeaveData] = useState({
    employeeName: isAdmin ? "" : username || user.name,
    department: user.department2 || "",
    leaveType: "",
    startDate: "",
    endDate: "",
  });
  const [existingLeaveRequests, setExistingLeaveRequests] = useState([]);
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    if (username) {
      fetchLeaveRequests(username);
    }
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin, username]);

  const fetchLeaveRequests = async (username) => {
    try {
      const response = await axios.get(
        "https://backenderpforseg.samielmetwali.com/getleaveRequest.php"
      );
      const leaveRequests = response.data.data;
      const userLeaveRequests = leaveRequests.filter(
        (request) =>
          request.username === username &&
          request.leave_type !== "إجازة أسبوعية"
      );
      setExistingLeaveRequests(userLeaveRequests);
      return userLeaveRequests; // Return the fetched data
    } catch (error) {
      console.error("Error fetching leave requests:", error);
      return []; // Return empty array on error
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(
        "https://backenderpforseg.samielmetwali.com/getemployees.php"
      );
      const all = response.data.data || [];
      setEmployees(all);
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "employeeName") {
      const selectedEmployee = employees.find((emp) => emp.name === value);

      if (selectedEmployee) {
        setLeaveData((prevData) => ({
          ...prevData,
          employeeName: value,
          username: selectedEmployee.username,
          department: selectedEmployee.department,
        }));
      } else {
        setLeaveData((prevData) => ({
          ...prevData,
          employeeName: value,
          department: "",
          username: "",
        }));
      }
    } else {
      setLeaveData((prevData) => ({
        ...prevData,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const { startDate, endDate, employeeName, department, leaveType } =
      leaveData;
    if (
      !employeeName.trim() ||
      !department.trim() ||
      !leaveType.trim() ||
      !startDate ||
      !endDate
    ) {
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: "جميع الحقول مطلوبة.",
      });
      return;
    }

    const selectedUsername = isAdmin ? leaveData.username : username;

    const userLeaveRequests = await fetchLeaveRequests(selectedUsername);

    // عد فقط الطلبات المقبولة من القائد
    const acceptedLeaves = userLeaveRequests.filter(
      (request) => request.is_accepted === 1 || request.is_accepted === "1"
    );
    // Check total existing requests
    if (acceptedLeaves.length >= 2) {
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: "لا يمكنك تقديم أكثر من 2 طلب إجازة في الشهر.",
      });
      return;
    }

    // تحديد head_accepted إذا كان الموظف قائد فريق
    let headAcceptedValue = null;
    let emp = null;
    if (isAdmin && employees.length > 0) {
      emp = employees.find((e) => e.username === leaveData.username);
    } else {
      emp = user;
    }
    if (emp && ["قائد فريق", "رئيس"].includes((emp.position || "").trim())) {
      headAcceptedValue = 1;
    }

    const formData = {
      ...leaveData,
      username: selectedUsername,
      ...(headAcceptedValue === 1 && { head_accepted: 1 }),
    };

    try {
      await axios.post(
        "https://backenderpforseg.samielmetwali.com/postLeaveRequest.php",
        formData
      );
      Swal.fire({
        icon: "success",
        title: "تم إرسال طلب الإجازة بنجاح",
        text: "تم تقديم طلب الإجازة بنجاح.",
      });
      await fetchLeaveRequests(selectedUsername);
      setLeaveData({
        employeeName: isAdmin ? "" : username || "",
        department: "",
        leaveType: "",
        leaveReason: "",
        startDate: "",
        endDate: "",
      });
      if (onSuccess) {
        onSuccess(formData);
      }
    } catch (error) {
      let errorMessage = "حدث خطأ ما. حاول مرة أخرى.";
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      Swal.fire({
        icon: "error",
        title: "خطأ",
        text: errorMessage,
      });
    }
  };

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          p={2}
          sx={{
            direction: "rtl",
            fontFamily: "Cairo, sans-serif",
            height: "100%",
            maxWidth: 600,
            margin: "auto",
            mt: 4,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              padding: 4,
              borderRadius: 4,
              boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              background: "linear-gradient(135deg, #f8fafc 0%, #e0e7ef 100%)",
            }}
          >
            <form onSubmit={handleSubmit} style={{ height: "100%" }}>
              <Grid container spacing={2} style={{ height: "100%" }}>
                <Grid item xs={12}>
                  {isAdmin && (
                    <FormControl fullWidth>
                      <InputLabel id="employee-select-label">
                        اسم الموظف
                      </InputLabel>
                      <Select
                        labelId="employee-select-label"
                        name="employeeName"
                        value={leaveData.employeeName}
                        onChange={handleChange}
                      >
                        {employees.map((emp) => (
                          <MenuItem key={emp.id} value={emp.name}>
                            {emp.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel id="leave-type-label">نوع الإجازة</InputLabel>
                    <Select
                      labelId="leave-type-label"
                      name="leaveType"
                      value={leaveData.leaveType}
                      onChange={handleChange}
                    >
                      <MenuItem value="اعتيادي">اعتيادي</MenuItem>
                      <MenuItem value="عارضة">عارضة</MenuItem>
                      <MenuItem value="مرضية">مرضية</MenuItem>
                      <MenuItem value="بدون أجر">بدون أجر</MenuItem>
                      <MenuItem value="مهمة عمل">مهمة عمل</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="تاريخ البدء"
                    name="startDate"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={leaveData.startDate}
                    onChange={handleChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&:hover fieldset": { borderColor: "#06b6d4" },
                        "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="تاريخ الانتهاء"
                    name="endDate"
                    type="date"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={leaveData.endDate}
                    onChange={handleChange}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                        "&:hover fieldset": { borderColor: "#06b6d4" },
                        "&.Mui-focused fieldset": { borderColor: "#6366f1" },
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      mt: 2,
                      py: 1.5,
                      fontWeight: 600,
                      background:
                        "linear-gradient(90deg, #6366f1 0%, #06b6d4 100%)",
                      color: "#fff",
                      borderRadius: 2,
                      boxShadow: "0 2px 8px rgba(6,182,212,0.15)",
                      "&:hover": {
                        background:
                          "linear-gradient(90deg, #06b6d4 0%, #6366f1 100%)",
                      },
                    }}
                  >
                    إرسال
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Paper>
        </Box>
      </ThemeProvider>
    </CacheProvider>
  );
};

export default LeaveRequestForm;
